import { useState, useEffect } from "react";
// Force rebuild - v2
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import BookDetail from "@/pages/BookDetail";
import ChapterView from "@/pages/ChapterView";
import GetCredits from "@/pages/GetCredits";
import MultiBookSeries from "@/pages/MultiBookSeries";
import ScriptCreator from "@/pages/ScriptCreator";
import ScriptDetail from "@/pages/ScriptDetail";
import CoverCreator from "@/pages/CoverCreator";
import EbookCreator from "@/pages/EbookCreator";
import AudiobookCreator from "@/pages/AudiobookCreator";
import AdminPortal from "@/pages/AdminPortal";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import HelpCenter from "@/pages/HelpCenter";
import AuthCallback from "@/components/AuthCallback";

// Helper function to check if we're in preview environment
// AUDIOBOOK DISABLED FOR PRODUCTION - not production ready, still timing out
const isPreviewEnvironment = () => {
  const hostname = window.location.hostname;
  // Preview environments use emergent preview URLs or localhost
  return hostname.includes('preview') || 
         hostname.includes('localhost') || 
         hostname.includes('127.0.0.1') ||
         hostname.includes('emergent');
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  // Only add token if it exists and is not the string "null" or "undefined"
  if (token && token !== "null" && token !== "undefined") {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Axios response interceptor for automatic retry on network errors
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    
    // On 401/403, clear token and let the component handle redirect
    if (error?.response?.status === 401 || error?.response?.status === 403) {
      // Don't clear token here - let components decide
      return Promise.reject(error);
    }
    
    // Only retry on network errors or 5xx errors, not on 4xx client errors
    const shouldRetry = !error.response || (error.response.status >= 500 && error.response.status < 600);
    
    // Don't retry if we've already retried or if it's a POST/PUT/DELETE (to avoid duplicate actions)
    if (!shouldRetry || config._retry || ['POST', 'PUT', 'DELETE'].includes(config.method?.toUpperCase())) {
      return Promise.reject(error);
    }
    
    config._retry = true;
    console.log(`[Axios] Retrying failed request: ${config.method?.toUpperCase()} ${config.url}`);
    
    // Wait 1 second before retrying
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    return axios(config);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAudiobook, setShowAudiobook] = useState(false);

  useEffect(() => {
    checkAuth();
    // Check if we're in preview environment
    setShowAudiobook(isPreviewEnvironment());
  }, []);

  const checkAuth = async (retryCount = 0) => {
    const token = localStorage.getItem("token");
    if (!token || token === "null" || token === "undefined") {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      setLoading(false);
    } catch (error) {
      console.error("[Auth] checkAuth failed:", error?.response?.status, error?.message);
      
      // Only clear token on explicit 401 Unauthorized, not on network errors
      if (error?.response?.status === 401) {
        console.log("[Auth] Token invalid (401), clearing");
        localStorage.removeItem("token");
        setLoading(false);
      } else if (retryCount < 2) {
        // Network error or server error - retry
        console.log(`[Auth] Retrying checkAuth (attempt ${retryCount + 2}/3)...`);
        setTimeout(() => checkAuth(retryCount + 1), 1000 * (retryCount + 1));
      } else {
        // After retries failed, keep token but show logged out state
        // User can try refreshing again
        console.log("[Auth] All retries failed, but keeping token");
        setLoading(false);
      }
    }
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem("token", token);
    setUser(userData);
    toast.success("Welcome back!");
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setUser(null);
    toast.success("Logged out successfully");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-stone-100">
        <div className="text-lg font-medium text-stone-700">Loading...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <AppRouter 
          user={user} 
          handleLogin={handleLogin} 
          handleLogout={handleLogout}
          showAudiobook={showAudiobook}
        />
      </BrowserRouter>
    </div>
  );
}

/**
 * AppRouter handles the routing logic and detects session_id synchronously.
 * CRITICAL: Check URL fragment for session_id during render (NOT in useEffect)
 * to prevent race conditions with ProtectedRoutes.
 * 
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
function AppRouter({ user, handleLogin, handleLogout, showAudiobook }) {
  const location = useLocation();
  
  // Synchronous check for session_id in URL fragment
  // This MUST happen during render, not in useEffect, to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback onLogin={handleLogin} />;
  }
  
  return (
    <Routes>
      <Route
        path="/"
        element={
          user ? (
            <Navigate to="/dashboard" />
          ) : (
            <Landing onLogin={handleLogin} />
          )
        }
      />
      <Route
        path="/forgot-password"
        element={<ForgotPassword />}
      />
      <Route
        path="/reset-password"
        element={<ResetPassword />}
      />
      <Route
        path="/dashboard"
        element={
          user ? (
            <Dashboard user={user} onLogout={handleLogout} />
          ) : (
            <Navigate to="/" />
          )
        }
      />
      <Route
        path="/book/:bookId"
        element={
          user ? <BookDetail user={user} /> : <Navigate to="/" />
        }
      />
      <Route
        path="/chapter/:chapterId"
        element={
          user ? <ChapterView user={user} /> : <Navigate to="/" />
        }
      />
      <Route
        path="/credits"
        element={
          user ? <GetCredits /> : <Navigate to="/" />
        }
      />
      <Route
        path="/series"
        element={
          user ? <MultiBookSeries /> : <Navigate to="/" />
        }
      />
      <Route
        path="/script-creator"
        element={
          user ? <ScriptCreator /> : <Navigate to="/" />
        }
      />
      <Route
        path="/script/:scriptId"
        element={
          user ? <ScriptDetail /> : <Navigate to="/" />
        }
      />
      <Route
        path="/ebook-creator"
        element={
          user ? <EbookCreator /> : <Navigate to="/" />
        }
      />
      {showAudiobook && (
        <Route
          path="/audiobook-creator"
          element={
            user ? <AudiobookCreator /> : <Navigate to="/" />
          }
        />
      )}
      <Route
        path="/cover-creator"
        element={
          user ? <CoverCreator /> : <Navigate to="/" />
        }
      />
      <Route
        path="/admin"
        element={
          user ? <AdminPortal /> : <Navigate to="/" />
        }
      />
      <Route
        path="/help"
        element={
          user ? <HelpCenter /> : <Navigate to="/" />
        }
      />
    </Routes>
  );
}

export default App;